/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\BlacklistGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { Ban } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { BlacklistType, BlacklistScope, BlacklistSource, BlacklistEntryFormState } from '../types';

type BlacklistFormData = BlacklistEntryFormState;

interface SelectOption {
    value: string;
    label: string;
}

interface BlacklistGeneralSectionProps {
    formData: BlacklistFormData;
    errors: Record<string, string>;
    typeOptions: SelectOption[];
    scopeOptions: SelectOption[];
    sourceOptions: SelectOption[];
    onFieldChange: <K extends keyof BlacklistFormData>(field: K, value: BlacklistFormData[K]) => void;
    t: (key: string) => string;
}

export function BlacklistGeneralSection({
    formData, errors, typeOptions, scopeOptions, sourceOptions, onFieldChange, t,
}: BlacklistGeneralSectionProps) {
    return (
        <FormBox title={t('blacklist.sections.general')} icon={<Ban className="h-5 w-5" />}>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

                {/* Left column */}
                <div className="space-y-4">
                    {/* Type */}
                    <div className="space-y-2">
                        <Label htmlFor="type" className="flex items-center gap-1">
                            <SimpleTooltip content={t('blacklist.fields.type.tooltip')}>
                                <span>{t('blacklist.fields.type.label')}</span>
                            </SimpleTooltip>
                            <span className="text-destructive">*</span>
                        </Label>
                        <select
                            id="type"
                            value={formData.type}
                            onChange={(e) => onFieldChange('type', e.target.value as BlacklistType)}
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            {typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>

                    {/* Value */}
                    <div className="space-y-2">
                        <Label htmlFor="value" className="flex items-center gap-1">
                            <SimpleTooltip content={t('blacklist.fields.value.tooltip')}>
                                <span>{t('blacklist.fields.value.label')}</span>
                            </SimpleTooltip>
                            <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="value"
                            value={formData.value}
                            onChange={(e) => onFieldChange('value', e.target.value)}
                            placeholder={t('blacklist.fields.value.placeholder')}
                            className={errors.value ? 'border-destructive' : undefined}
                        />
                        {errors.value && <p className="text-sm text-destructive">{errors.value}</p>}
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="description">
                            <SimpleTooltip content={t('blacklist.fields.description.tooltip')}>
                                <span>{t('blacklist.fields.description.label')}</span>
                            </SimpleTooltip>
                        </Label>
                        <Input
                            id="description"
                            value={formData.description}
                            onChange={(e) => onFieldChange('description', e.target.value)}
                            placeholder={t('blacklist.fields.description.placeholder')}
                        />
                    </div>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                    {/* Scope */}
                    <div className="space-y-2">
                        <Label htmlFor="scope">
                            <SimpleTooltip content={t('blacklist.fields.scope.tooltip')}>
                                <span>{t('blacklist.fields.scope.label')}</span>
                            </SimpleTooltip>
                        </Label>
                        <select
                            id="scope"
                            value={formData.scope}
                            onChange={(e) => onFieldChange('scope', e.target.value as BlacklistScope)}
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            {scopeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>

                    {/* Source */}
                    <div className="space-y-2">
                        <Label htmlFor="source">
                            <SimpleTooltip content={t('blacklist.fields.source.tooltip')}>
                                <span>{t('blacklist.fields.source.label')}</span>
                            </SimpleTooltip>
                        </Label>
                        <select
                            id="source"
                            value={formData.source}
                            onChange={(e) => onFieldChange('source', e.target.value as BlacklistSource)}
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            {sourceOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>

                    {/* Enabled */}
                    <div className="space-y-2">
                        <Label htmlFor="enabled">
                            <SimpleTooltip content={t('blacklist.fields.enabled.tooltip')}>
                                <span>{t('blacklist.fields.enabled.label')}</span>
                            </SimpleTooltip>
                        </Label>
                        <Toggle
                            id="enabled"
                            value={formData.enabled}
                            onChange={(value) => onFieldChange('enabled', value)}
                        />
                    </div>
                </div>

            </div>
        </FormBox>
    );
}
